import {
    Operation,
    ApolloLink,
    RequestHandler,
    NextLink,
    Observable,
    FetchResult,
} from '@apollo/client/core';

export type RouterLinkRoutes<K extends string = string> = Record<
    K,
    ApolloLink | RequestHandler
>;

const emptyFetchResult: Observable<FetchResult> = Observable.from([]);

export default class RouterLink<K extends string = string> extends ApolloLink {
    constructor(
        public readonly name: string | null,
        public routes: RouterLinkRoutes<K>,
        public defaultRoute: (op: Operation) => K | null | undefined
    ) {
        super();
    }

    public request(
        operation: Operation,
        forward?: NextLink
    ): Observable<FetchResult> {
        const context = operation.getContext();

        const forwardTo = (route: K | null): Observable<FetchResult> | null => {
            const upstreamRoutes = context.upstreamRoutes ?? [];
            upstreamRoutes.push({ router: this, route });
            operation.setContext({ upstreamRoutes });

            if (route === null) {
                return forward ? forward(operation) : null;
            } else {
                const link = this.routes[route];
                if (link instanceof ApolloLink) {
                    return link.request(operation, forward);
                } else if (forward) {
                    return link(operation, forward ?? (() => emptyFetchResult));
                } else {
                    return null;
                }
            }
        };

        if (this.hasRouteName(context.via))
            return forwardTo(context.via) ?? emptyFetchResult;

        if (Array.isArray(context.via)) {
            for (const viaItem of context.via) {
                if (this.hasRouteName(viaItem))
                    return forwardTo(viaItem) ?? emptyFetchResult;
                if (viaItem && typeof viaItem === 'object') {
                    const { router, route } = viaItem;
                    if (
                        this.isReferencedBy(router) &&
                        this.hasRouteName(route)
                    ) {
                        return forwardTo(route) ?? emptyFetchResult;
                    }
                }
            }
        }

        return (
            forwardTo(this.defaultRoute(operation) ?? null) ?? emptyFetchResult
        );
    }

    private isReferencedBy(value: unknown): boolean {
        return (
            value === null ||
            value === undefined ||
            value === this.name ||
            value === this
        );
    }

    public hasRouteName(value: unknown): value is K {
        return typeof value === 'string' && value in this.routes;
    }
}

export type RouterLinkDefaultRoute<K extends string = string> =
    | K
    | null
    | undefined
    | ((op: Operation) => K | null | undefined);

export function router<K extends string = string>(
    routes: RouterLinkRoutes<K>,
    defaultRoute?: RouterLinkDefaultRoute<K>
): RouterLink<K>;

export function router<K extends string = string>(
    name: string | null,
    routes: RouterLinkRoutes<K>,
    defaultRoute?: RouterLinkDefaultRoute<K>
): RouterLink<K>;

export function router<K extends string = string>(
    nameOrRoutes: string | null | RouterLinkRoutes<K>,
    routesOrDefault?: RouterLinkRoutes<K> | RouterLinkDefaultRoute<K>,
    defaultRoute?: RouterLinkDefaultRoute<K>
): RouterLink<K> {
    let name: string | null = null;
    let routes: RouterLinkRoutes<K>;

    if (typeof nameOrRoutes === 'string' || nameOrRoutes === null) {
        name = nameOrRoutes as string | null;
        routes = routesOrDefault as RouterLinkRoutes<K>;
    } else {
        routes = nameOrRoutes as RouterLinkRoutes<K>;
        defaultRoute = routesOrDefault as RouterLinkDefaultRoute<K>;
    }

    if (typeof defaultRoute !== 'function') {
        const defaultRouteValue = defaultRoute;
        defaultRoute = () => defaultRouteValue;
    }

    return new RouterLink(name, routes, defaultRoute);
}
